import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  FileText,
  X,
} from "lucide-react";
import { documentsApi } from "@/api/documents";
import { Badge, Button, Input } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import type { DocumentDto, IngestionStatus } from "@/types/api";

interface MasterDocumentPickerProps {
  open: boolean;
  onClose: () => void;
  onPick: (doc: DocumentDto) => void;
  /** Currently-selected master document id, so the row can highlight. */
  selectedId?: string | null;
}

/**
 * v3.1 hotfix — modal for picking the master document on a report
 * template. Replaces the raw GUID textbox that admins were never going
 * to remember IDs for. Lists documents from the central repository
 * (the same data source the DMS pages use), with a name search and an
 * ingestion-status badge per row so admins can see at a glance whether
 * a document is ready to be referenced from a generation. Only ingested
 * documents are selectable — non-ingested ones would fail prompt
 * resolution anyway.
 */
export function MasterDocumentPicker({
  open,
  onClose,
  onPick,
  selectedId,
}: MasterDocumentPickerProps) {
  const [search, setSearch] = useState("");

  const list = useQuery({
    queryKey: ["master-document-picker", search],
    queryFn: () =>
      documentsApi.list({
        page: 1,
        pageSize: 100,
        q: search || undefined,
        sortBy: "name",
        sortDirection: "asc",
      }),
    enabled: open,
  });

  const docs = useMemo(() => list.data?.data ?? [], [list.data]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="Choose master document"
      description="Pick a document from the central repository to attach as this template's master document. Only ingested documents are selectable."
      size="lg"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
      }
    >
      <div className="space-y-space-3">
        <Input
          type="search"
          placeholder="Search documents by name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {list.isLoading ? (
          <p className="text-caption text-gray-mid">Loading documents…</p>
        ) : list.isError ? (
          <p className="text-caption text-status-error" role="alert">
            {(list.error as Error).message}
          </p>
        ) : docs.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-caption text-gray-mid py-space-5">
            <FileText size={28} aria-hidden className="mb-space-2" />
            <p>{search ? "No documents match." : "No documents available."}</p>
          </div>
        ) : (
          <ul
            className="flex flex-col gap-space-1 max-h-[28rem] overflow-y-auto"
            role="listbox"
            aria-label="Central repository documents"
          >
            {docs.map((doc) => (
              <li key={doc.id}>
                <DocumentRow
                  doc={doc}
                  selected={doc.id === selectedId}
                  onPick={() => {
                    onPick(doc);
                    onClose();
                  }}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}

function DocumentRow({
  doc,
  selected,
  onPick,
}: {
  doc: DocumentDto;
  selected: boolean;
  onPick: () => void;
}) {
  const ingested =
    doc.ingestionStatus === "Completed" || doc.ingestionStatus === "Ingested";
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      disabled={!ingested}
      onClick={onPick}
      title={
        ingested
          ? undefined
          : `Document ingestion is ${doc.ingestionStatus.toLowerCase()}. Wait for ingestion to complete before selecting.`
      }
      className={`w-full text-left flex items-center gap-space-3 px-space-3 py-space-2 rounded-md border transition-colors ${
        selected
          ? "bg-gold/10 border-gold"
          : "border-border-light hover:bg-white-warm hover:border-gold-shadow"
      } ${ingested ? "" : "opacity-60 cursor-not-allowed"}`}
    >
      <FileText
        size={18}
        aria-hidden
        className={selected ? "text-gold-shadow" : "text-gray-mid"}
      />
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-onyx truncate">{doc.title}</div>
        <div className="text-caption text-gray-mid truncate">
          {fileTypeLabel(doc)} · {formatBytes(doc.fileSizeBytes)}
          {doc.folderName ? ` · ${doc.folderName}` : ""}
        </div>
      </div>
      <IngestionBadge status={doc.ingestionStatus} />
      <Badge status="info">{doc.category}</Badge>
    </button>
  );
}

function IngestionBadge({ status }: { status: IngestionStatus }) {
  if (status === "Completed" || status === "Ingested") {
    return (
      <span className="inline-flex items-center gap-space-1 text-caption text-status-success whitespace-nowrap">
        <CheckCircle2 size={12} aria-hidden /> Ingested
      </span>
    );
  }
  if (status === "Pending") {
    return (
      <span className="inline-flex items-center gap-space-1 text-caption text-status-warning whitespace-nowrap">
        <Clock size={12} aria-hidden /> Pending
      </span>
    );
  }
  if (status === "Failed") {
    return (
      <span className="inline-flex items-center gap-space-1 text-caption text-status-error whitespace-nowrap">
        <AlertCircle size={12} aria-hidden /> Failed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-space-1 text-caption text-gray-mid whitespace-nowrap">
      <X size={12} aria-hidden /> Not ingested
    </span>
  );
}

function fileTypeLabel(doc: DocumentDto): string {
  if (doc.fileExtension) {
    return doc.fileExtension.replace(/^\./, "").toUpperCase();
  }
  const slash = doc.contentType.lastIndexOf("/");
  return slash >= 0
    ? doc.contentType.slice(slash + 1).toUpperCase()
    : doc.contentType.toUpperCase();
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return `${value.toFixed(value >= 10 || unit === 0 ? 0 : 1)} ${units[unit]}`;
}
